/**
 * v0.9.4a — "ระบายเพิ่ม/ลด" vs the previous SWOC report: shared wording for the
 * map arrow, the dam card and the result-card bar. Every number comes from
 * dams-status.json (`prev` is written by scripts/dam_history.mjs from the real
 * report history); no previous report → nothing is compared (A5).
 */
import { releaseChange, percentChange, levelStreak, type ReleaseChange, type PercentChange, type Streak } from "@flowsight/sim";

/** A6 — first mention in a card uses the full name; narrow spots use SRC_SHORT. */
export const SRC_FULL = "กรมชลประทาน (ศูนย์ปฏิบัติการน้ำอัจฉริยะ SWOC)";
export const SRC_SHORT = "กรมชลฯ (SWOC)";

export interface DamPrev { valueDate: string; src?: string; percent?: number | null; inflowCms?: number | null; outflowCms?: number | null }
export interface C13Prev { valueDate: string; outflow_cms: number | null }
export interface C13Recent { valueDate: string; outflow_cms: number | null }

const THAI_MON = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
/** "2026-10-02" → "2 ต.ค." (short; the card's own data line carries the year). */
export function thaiDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${Number(m[3])} ${THAI_MON[Number(m[2]) - 1] ?? ""}` : iso;
}
/** ลบ.ม./วิ: whole numbers from 100 up, one decimal below (5.8 stays 5.8). */
export function fmtCms(v: number): string {
  return v.toLocaleString("en-US", { maximumFractionDigits: Math.abs(v) >= 100 ? 0 : 1 });
}

export function damReleaseChange(d: { valueDate?: string; outflowCms: number | null; prev?: DamPrev | null }): ReleaseChange | null {
  if (!d.valueDate || !d.prev) return null;
  return releaseChange({ valueDate: d.valueDate, value: d.outflowCms }, { valueDate: d.prev.valueDate, value: d.prev.outflowCms ?? null }, "dam");
}
export function damPercentChange(d: { valueDate?: string; percent: number | null; prev?: DamPrev | null }): PercentChange | null {
  if (!d.valueDate || !d.prev) return null;
  return percentChange({ valueDate: d.valueDate, value: d.percent }, { valueDate: d.prev.valueDate, value: d.prev.percent ?? null });
}
export function c13Change(c: { valueDate?: string | null; outflow_cms: number | null; prev?: C13Prev | null } | null | undefined): ReleaseChange | null {
  if (!c?.valueDate || !c.prev) return null;
  return releaseChange({ valueDate: c.valueDate, value: c.outflow_cms }, { valueDate: c.prev.valueDate, value: c.prev.outflow_cms }, "c13");
}

/** C.13 "ระบายระดับนี้ต่อเนื่อง N วัน" from the real report history (null = not enough history). */
export function c13Streak(c: { valueDate?: string | null; outflow_cms: number | null; recent?: C13Recent[] | null } | null | undefined): Streak | null {
  if (!c?.valueDate || !c.recent) return null;
  return levelStreak(c.recent.map((x) => ({ valueDate: x.valueDate, value: x.outflow_cms })), { valueDate: c.valueDate, value: c.outflow_cms });
}

/** "เมื่อวาน" only when the previous report is exactly the day before, else its date. */
const since = (ch: { isYesterday: boolean; prevDate: string }): string => (ch.isYesterday ? "เมื่อวาน" : `รายงาน ${thaiDay(ch.prevDate)}`);

/** Dam card: significant → "▲ เพิ่มจากเมื่อวาน 170" / "▼ ลดจากเมื่อวาน 200";
 *  smaller → plain "(เมื่อวาน 0)"; equal → "เท่ากับเมื่อวาน". */
export function releaseDeltaText(ch: ReleaseChange): string {
  if (ch.dir === "same") return `เท่ากับ${since(ch)}`;
  if (!ch.arrow) return `(${since(ch)} ${fmtCms(ch.prev)})`;
  return `${ch.dir === "up" ? "▲ เพิ่ม" : "▼ ลด"}จาก${since(ch)} ${fmtCms(Math.abs(ch.delta))}`;
}
/** Dam card: "▲ จาก 110%" / "เท่ากับเมื่อวาน". */
export function percentDeltaText(ch: PercentChange): string {
  if (ch.dir === "same") return `เท่ากับ${since(ch)}`;
  return `${ch.dir === "up" ? "▲" : "▼"} จาก ${Math.round(ch.prev)}%${ch.isYesterday ? "" : ` (${thaiDay(ch.prevDate)})`}`;
}
/** Result-card line (name already includes "เขื่อน"):
 *  significant rise (amber)  "เขื่อน<ชื่อ>ระบายเพิ่มจากเมื่อวาน X → Y ลบ.ม./วิ"
 *  significant fall (green)  "เขื่อน<ชื่อ>ระบายลดลงจากเมื่อวาน X → Y ลบ.ม./วิ"
 *  equal (plain)             "เขื่อน<ชื่อ> ระบาย Y ลบ.ม./วิ เท่ากับเมื่อวาน"
 *  smaller change (plain)    "เขื่อน<ชื่อ> ระบาย Y ลบ.ม./วิ (เมื่อวาน X)" */
export function releaseBarText(name: string, ch: ReleaseChange): string {
  if (ch.dir === "same") return `${name} ระบาย ${fmtCms(ch.cur)} ลบ.ม./วิ เท่ากับ${since(ch)}`;
  if (!ch.arrow) return `${name} ระบาย ${fmtCms(ch.cur)} ลบ.ม./วิ (${since(ch)} ${fmtCms(ch.prev)})`;
  return `${name}${ch.dir === "up" ? "ระบายเพิ่มจาก" : "ระบายลดลงจาก"}${since(ch)} ${fmtCms(ch.prev)} → ${fmtCms(ch.cur)} ลบ.ม./วิ`;
}
